import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

interface CountyInsights {
  averageWeeklyCost: string;
  fairDealInsight: string;
  keyFacts: string[];
  whyChooseCounty: string;
  nearbyHospitals: string[];
  transportLinks: string;
  lastUpdated: string;
}

interface UseCountyInsightsResult {
  insights: CountyInsights | null;
  citations: string[];
  isLoading: boolean;
  error: string | null;
}

const CACHE_KEY_PREFIX = "grandpal_county_insights_";
const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

export function useCountyInsights(countyName: string, countySlug?: string): UseCountyInsightsResult {
  const [insights, setInsights] = useState<CountyInsights | null>(null);
  const [citations, setCitations] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!countyName) return;

    const cacheKey = `${CACHE_KEY_PREFIX}${countySlug || countyName.toLowerCase()}`;

    // Check localStorage cache first
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < CACHE_TTL_MS) {
          setInsights(data.insights);
          setCitations(data.citations || []);
          return;
        }
      }
    } catch (_) {
      // ignore cache errors
    }

    const fetchInsights = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const { data, error: fnError } = await supabase.functions.invoke("county-insights", {
          body: { countyName, countySlug },
        });

        if (fnError) throw new Error(fnError.message);

        setInsights(data.insights);
        setCitations(data.citations || []);

        // Cache the result
        try {
          localStorage.setItem(cacheKey, JSON.stringify({
            data: { insights: data.insights, citations: data.citations },
            timestamp: Date.now(),
          }));
        } catch (_) {
          // ignore storage errors
        }
      } catch (err) {
        console.error("County insights fetch error:", err);
        setError(err instanceof Error ? err.message : "Failed to load insights");
      } finally {
        setIsLoading(false);
      }
    };

    fetchInsights();
  }, [countyName, countySlug]);

  return { insights, citations, isLoading, error };
}
